import { ProfileFilter } from "@happy-legal/graphql-types";
import { Box, Button, Chip, Grid, TextField, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/SendOutlined";
import { isEqual } from "lodash";
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Descendant } from "slate";
import { graphQlClient } from "../shared/clients";
import { errorMessage } from "../shared/consts";
import { LoadingContext } from "../shared/providers/LoadingProvider";
import { confirm, logger, notify } from "../shared/utils";
import NewsletterEditor from "./components/NewsletterEditor/NewsletterEditor";
import { serializeHtml } from "./components/NewsletterEditor/serializers";
import { ejsRender } from "./components/NewsletterEditor/serializers/serializeHtml";
import {
  defaultDocument,
  emptyDocument,
} from "./components/NewsletterEditor/templates";
import { getEjsTemplateFromS3 } from "./components/NewsletterEditor/utils";
import NewsletterFilter from "./components/NewsletterFilter";
import NewsletterPreview from "./components/NewsletterPreview";
import { QUERY_SEND_NEWSLETTER } from "./gql/queries";

const Newsletter = () => {
  // Email content
  const [content, setContent] = useState<Descendant[]>(defaultDocument);

  // Subject field state, ref and handler
  const [subject, setSubject] = useState<string>("");
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const handleSubject = (event: ChangeEvent<HTMLInputElement>) => {
    setSubject(event.target.value);
  };

  // Retrieve email templates on render
  const [templates, setTemplates] = useState<{
    email: string;
    footer: string;
  }>({ email: "", footer: "" });
  useEffect(() => {
    const getEmail = getEjsTemplateFromS3("HtmlBase.ejs");
    const getFooter = getEjsTemplateFromS3("Elements/Footer.ejs");

    const getTemplates = () => {
      Promise.all([getEmail, getFooter]).then(([email, footer]) => {
        setTemplates((prevTemplates) => ({
          ...prevTemplates,
          email: email || "",
          footer: footer || "",
        }));
      });
    };
    getTemplates();
  }, []);

  // Serialize newsletter into ejs templates
  const getRenderedEmail = (isPreview: boolean) => {
    const { email, footer } = templates;

    // Templates load asynchronously from S3; until they arrive, skip
    // rendering rather than passing empty strings into ejsRender (which
    // would produce a blank preview and, on older code paths, could throw).
    if (!email || !footer) {
      return "";
    }

    try {
      return ejsRender(email, {
        type: serializeHtml(content, isPreview),
        footer,
      });
    } catch (error) {
      logger("Newsletter template rendering error", error);
      return "";
    }
  };

  const { showLoading, hideLoading } = useContext(LoadingContext);

  // In case of sudden unmount
  useEffect(() => {
    return hideLoading;
  }, []);

  // Recipient filters
  const [filter, setFilter] = useState<ProfileFilter>({});
  const [notEqual, setNotEqual] = useState<ProfileFilter>({});

  const getRecipient = () => {
    const onlySubscribers = notEqual?.abos === null;
    const onlyNonSubscribers = filter?.abos === null;
    let recipient = onlySubscribers
      ? "nur Abonnenten"
      : onlyNonSubscribers
      ? "nur Nicht-Abonnenten"
      : "alle Kunden";

    const city = filter?.rentalObjects?.[0]?.address?.city;
    if (city) {
      recipient += ` in ${city}`;
    }

    return recipient;
  };

  const sendEmail = async () => {
    let hasError = false;

    // Attempting to send either empty/default document
    if (isEqual(content, defaultDocument) || isEqual(content, emptyDocument)) {
      hasError = true;
      notify("Kann keine Vorlage/leeren Newsletter versenden.");
    }

    // No subject was entered
    if (!subject) {
      hasError = true;
      notify("Es wurde kein Betreff eingegeben.");
      subjectRef.current?.focus();
    }

    if (hasError) return;

    const isSure = await confirm({
      title: "Newsletter versenden",
      message: `Sie sind dabei, den «${subject}»-Newsletter an ${getRecipient()} zu versenden. Sind Sie sicher?`,
      confirmLabel: "Versenden",
    });

    if (!isSure) return;

    showLoading();

    const input = {
      subject,
      html: getRenderedEmail(false), //non-previewed
      // If filters chosen, add to input
      ...(!!Object.keys(filter).length && { filter }),
      ...(!!Object.keys(notEqual).length && { notEqual }),
    };

    const sent = await graphQlClient(QUERY_SEND_NEWSLETTER, input).catch(
      (err) => {
        const message = `Error sending newsletter.`;
        logger(message, err);
        notify(`${errorMessage}\n${message}`, "error");
      }
    );

    notify(
      sent
        ? "Newsletter wurde erfolgreich versendet"
        : `${errorMessage}\nNewsletter wurde nicht versendet.`
    );

    hideLoading();
  };

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {/* Composer */}
      <Grid size={{ xs: 12, lg: 7 }}>
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            backgroundColor: "background.paper",
            overflow: "hidden",
          }}
        >
          {/* Header: what is being sent, and the one action */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              px: 2,
              py: 1.5,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
              Newsletter verfassen
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={sendEmail}
            >
              Senden
            </Button>
          </Box>

          {/* Audience. The resolved recipient sits with the controls that
              decide it, which is what the old read-only "An" field was
              restating a second time further down the form. */}
          <Box
            sx={{
              px: 2,
              py: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: "background.subtle",
            }}
          >
            <NewsletterFilter
              filter={filter}
              setFilter={setFilter}
              notEqual={notEqual}
              setNotEqual={setNotEqual}
            />
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Geht an
              </Typography>
              <Chip label={getRecipient()} size="small" />
            </Box>
          </Box>

          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              variant="outlined"
              fullWidth
              label="Betreff"
              placeholder="Betreff des Newsletters"
              InputLabelProps={{ shrink: true }}
              inputRef={subjectRef}
              value={subject}
              onChange={handleSubject}
            />
            <NewsletterEditor value={content} onChange={setContent} />
          </Box>
        </Box>
      </Grid>

      {/* Live preview */}
      <Grid size={{ xs: 12, lg: 5 }}>
        <NewsletterPreview>
          <div
            dangerouslySetInnerHTML={{
              __html: getRenderedEmail(true),
            }}
          />
        </NewsletterPreview>
      </Grid>
    </Grid>
  );
};

export default Newsletter;
